import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FullCalendar from "@fullcalendar/react"; // must go before plugins
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import "./CalendarView.css";
import DashboardButtons from "../../../component/home/dashboard-actions";
import useDebounce from "../../../Hook/useDebounce";
import { useSelector, useDispatch } from "react-redux";
import Loader from "react-spinner-loader";
import moment from "moment";
import { SET_DATE_RANGE } from "../../../redux/constants";

export default function CalendarView(props) {
  const trigger = useRef(null);
  const dropdown = useRef(null);
  const calendarRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Modal selection variables
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState("");
  const [activityEventData, setActivityEventData] = useState("");
  const { company_id, email } = useParams();
  const { searchText } = useSelector((state) => state.overview);

  // Check if both company_id and email are present to determine embedded
  const isEmbedded = Boolean(company_id && email);
  const debouncedHandleSearch = useDebounce(searchText, 1000);

  useEffect(() => {
    props.setDebouncedSearchText(debouncedHandleSearch);
  }, [debouncedHandleSearch]);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  const renderEventContent = (eventInfo) => {
    return (
      <div className={`overflow-hidden cursor-pointer relative z-10`}>
        <div className="text-left text-xs font-medium text-gray-800 flex flex-row items-center w-[95%]">
          {eventInfo.event._def.extendedProps.type === "reminder" && (
            <span className="mr-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0M3.124 7.5A8.969 8.969 0 015.292 3m13.416 0a8.969 8.969 0 012.168 4.5"
                />
              </svg>
            </span>
          )}

          <span className="truncate text-gray-900">
            {eventInfo.event.title}
          </span>
          {eventInfo.event._def.extendedProps.is_locked === 1 && (
            <span
              className="fas fa-lock pl-1"
              style={{ color: "#8b8c8d" }}
            ></span>
          )}
        </div>
        <p className="truncate text-left font-normal text-xs text-gray-600">
          {eventInfo.event._def.extendedProps.description}
        </p>
      </div>
    );
  };

  const handleEventClick = (event) => {
    if (event.event?.extendedProps?.type === "reminder") {
      setSelectedReminder(event.event.id);
      setEventModalOpen(true);
    }
    if (event.event?.extendedProps?.type === "activity") {
      setActivityEventData(event.event);
    }
  };

  // Navigate to activity performance
  useEffect(() => {
    if (activityEventData) {
      if (!isEmbedded) {
        navigate(
          `/todo/performance/${activityEventData._def.extendedProps?.activity_assignment_id}?activity_id=${activityEventData._def.extendedProps?.activity_id}`
        );
      } else {
        navigate(
          `/activity/performance/embedded/${company_id}/${activityEventData._def.extendedProps.activity_assignment_id}/${email}?activity_id=${activityEventData._def.extendedProps?.activity_id}`
        );
      }
    }
  }, [activityEventData]);

  if (document.getElementsByClassName("fc-today-button")[0]?.innerHTML) {
    document.getElementsByClassName("fc-today-button")[0].innerHTML = "Today";
  }

  const handleNextAndPrev = (type) => {
    const calendarApi = calendarRef.current.getApi();
    if (type === "next") {
      calendarApi.next();
    } else {
      calendarApi.prev();
    }
    const start_date = moment(
      calendarApi?.currentDataManager?.data?.viewTitle,
      "MMMM YYYY"
    )
      .clone()
      .startOf("month")
      .format("YYYY-MM-DD");
    const end_date = moment(
      calendarApi?.currentDataManager?.data?.viewTitle,
      "MMMM YYYY"
    )
      .clone()
      .endOf("month")
      .format("YYYY-MM-DD");

    dispatch({
      type: SET_DATE_RANGE,
      payload: { start_date: start_date, end_date: end_date },
    });
  };

  useEffect(() => {
    dispatch({
      type: SET_DATE_RANGE,
      payload: {
        start_date: moment().clone().startOf("month").format("YYYY-MM-DD"),
        end_date: moment().clone().endOf("month").format("YYYY-MM-DD"),
      },
    });
  }, []);

  return (
    <div className="space-y-3">
      <DashboardButtons />
      <div className="py-2">
        <div
          className={`calendar-container relative ${
            props.loader ? "spinner-active" : ""
          }`}
        >
          <div className="flex items-center absolute right-0 top-[3px]">
            <button
              className="bg-[--bg-color-500] text-slate-50 hover:bg-[--bg-color-400] py-[5px] px-3 border border-[--bg-color-500] cursor-pointer rounded-tl rounded-bl"
              onClick={() => {
                handleNextAndPrev("prev");
              }}
            >
              <span className="fa-solid fa-chevron-left text-slate-50 text-base"></span>
            </button>
            <button
              className="bg-[--bg-color-500] text-slate-50 hover:bg-[--bg-color-400] py-[5px] px-3 border border-[--bg-color-500] cursor-pointer rounded-tr rounded-br"
              onClick={() => {
                handleNextAndPrev("next");
              }}
            >
              <span className="fa-solid fa-chevron-right text-slate-50 text-base"></span>
            </button>
          </div>

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            fixedWeekCount={false}
            eventClassNames="myclassname"
            events={props.events}
            eventContent={renderEventContent}
            dayMaxEventRows={true}
            eventClick={(event) => {
              handleEventClick(event);
            }}
            handleWindowResize={true}
            windowResize={function (arg) {
              console.log(
                "The calendar has adjusted to a window resize. Current view: " +
                  arg.view.type
              );
            }}
          />
          {props.loader && (
            <div className="flex justify-center p-2 my-2 whitespace-nowrap w-full calendar-spinner">
              <Loader show={props.loader} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
